import React, { useMemo, useState } from 'react';
import { components } from 'react-select';
import CreatableSelect from 'react-select/creatable';

// The catalog has a couple of thousand generics; rendering them all at once
// makes the menu sluggish, so only the best matches are shown.
const MAX_VISIBLE_OPTIONS = 100;

const toOption = (value) => ({ value, label: value });

const clean = (text) => String(text || '').trim().replace(/\s+/g, ' ');

// Values that start with the typed text first, then ones that merely contain it.
const matchValues = (values, input) => {
    const term = clean(input).toLowerCase();
    if (!term) return values;

    const startsWith = [];
    const contains = [];
    values.forEach((value) => {
        const index = value.toLowerCase().indexOf(term);
        if (index === 0) {
            startsWith.push(value);
        } else if (index > 0) {
            contains.push(value);
        }
    });
    return [...startsWith, ...contains];
};

// Styled to sit alongside the form's plain text inputs.
const selectStyles = {
    control: (base, state) => ({
        ...base,
        minHeight: '38px',
        fontSize: '0.875rem',
        borderRadius: '0.375rem',
        borderColor: '#e5e7eb',
        boxShadow: state.isFocused ? '0 0 0 2px #46A4A3' : 'none',
        '&:hover': { borderColor: '#e5e7eb' },
    }),
    option: (base, state) => ({
        ...base,
        fontSize: '0.875rem',
        backgroundColor: state.isSelected ? '#46A4A3' : state.isFocused ? '#f0fdfa' : '#fff',
        color: state.isSelected ? '#fff' : '#1f2937',
        cursor: 'pointer',
    }),
    placeholder: (base) => ({ ...base, color: '#9ca3af' }),
    // The form lives in a scrolling modal, so the menu is rendered in a portal above it.
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};

const MenuList = (props) => {
    const { hiddenCount } = props.selectProps;

    return (
        <components.MenuList {...props}>
            {props.children}
            {hiddenCount > 0 && (
                <div className="px-3 py-2 text-xs text-gray-500 border-t">
                    {hiddenCount} more not shown — type to search
                </div>
            )}
        </components.MenuList>
    );
};

/**
 * Dropdown of values already in the medicine catalog that also accepts a new one:
 * pick an existing value, or type a value that is not in the list.
 */
const MedicineOptionSelect = ({ inputId, value, options = [], onChange, placeholder }) => {
    const [inputValue, setInputValue] = useState('');

    const matches = useMemo(() => matchValues(options, inputValue), [options, inputValue]);
    const visibleOptions = useMemo(
        () => matches.slice(0, MAX_VISIBLE_OPTIONS).map(toOption),
        [matches]
    );

    // Typed text that differs from an existing value only by case or spacing
    // resolves to that value, so the catalog does not collect near-duplicates.
    const resolve = (text) => {
        const cleaned = clean(text);
        const existing = options.find((option) => option.toLowerCase() === cleaned.toLowerCase());
        return existing || cleaned;
    };

    const isNewValue = (text) => {
        const cleaned = clean(text).toLowerCase();
        if (!cleaned || cleaned === clean(value).toLowerCase()) return false;
        return !options.some((option) => option.toLowerCase() === cleaned);
    };

    // react-select drops typed text when the field loses focus; keep it instead,
    // so typing a new value and moving on works like a normal text input.
    const handleBlur = () => {
        if (clean(inputValue)) {
            onChange(resolve(inputValue));
        }
    };

    return (
        <CreatableSelect
            inputId={inputId}
            value={value ? toOption(value) : null}
            options={visibleOptions}
            filterOption={null}
            inputValue={inputValue}
            onInputChange={(text) => setInputValue(text)}
            onChange={(option) => onChange(option ? resolve(option.value) : '')}
            onBlur={handleBlur}
            isValidNewOption={isNewValue}
            formatCreateLabel={(text) => `Add new: "${clean(text)}"`}
            noOptionsMessage={() => 'Type to add a new value'}
            placeholder={placeholder}
            hiddenCount={matches.length - visibleOptions.length}
            components={{ MenuList }}
            styles={selectStyles}
            menuPortalTarget={document.body}
            menuPlacement="auto"
            tabSelectsValue={false}
            isClearable
        />
    );
};

export default MedicineOptionSelect;
